import 'intersection-observer';
import {
  BreakpointQueryName, Button, ButtonColor, Spacing, SpacingName, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import React from 'react';
import dynamic from 'next/dynamic';
import merge from 'lodash.merge';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import LandingLayout from '../client/layouts/landing-layout/LandingLayout';
import BottomBar from '../client/templates/common/bottom-bar/BottomBar';
import InfoSection, { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import { LatestSoldSectionProps } from '../client/templates/common/latest-sold-section/LatestSoldSection';
import { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import OurServicesSection, { OurServicesSectionProps } from '../client/templates/common/our-services-section/OurServicesSection';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { AboutUsSectionProps } from '../client/templates/common/about-us-section/AboutUsSection';
import { getServices } from '../services/our-services/ourServices';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import PageSection from '../client/components/page-section/PageSection';
import SavingCalculator from '../client/components/saving-calculator/SavingCalculator';
import {
  CTA, Feature, HeroSection, TextAlign,
} from '../client/types';
import ViewLatestSoldProperties from '../client/containers/view-latest-sold-properties/ViewLatestSoldProperties';
import { buildSoldPropertiesURLObject, LANDING_CITY_SELLER_AS } from '../client/utils/links';
import LogoList from '../client/components/logo-list/LogoList';
import Hero from '../client/templates/common/hero/Hero';
import { ReviewTarget } from '../domain/graphcms';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import { BottomBarProps } from '../client/templates/common/bottom-bar-v2/BottomBarV2';
import Stripe from '../client/components/stripe/Stripe';
import { getPartnersData } from '../services/partners/getPartnersData';
import getTrafficSource from '../client/utils/traffic-source/getTrafficSource';

const ListReviews = dynamic(() => import('../client/containers/list-reviews/ListReviews'), { ssr: false });

export interface LandingCitySellerProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  infoSection: InfoSectionProps,
  latestSoldProperties: LatestSoldSectionProps,
  aboutUsSection: AboutUsSectionProps,
  ourServicesSection: OurServicesSectionProps,
  press: LogosSectionProps & { cta?: CTA },
  savingCalculator: {
    title: string,
    intro: string,
    cta: CTA,
  },
  bottomBar: BottomBarProps,
  extraPackages?: {
    title: string,
    description: string,
    packages: Feature[],
  },
}

export const LandingCitySeller: NextPage<LandingCitySellerProps> = ({
  SEO,
  infoSection,
  latestSoldProperties,
  hero,
  aboutUsSection,
  ourServicesSection,
  press,
  savingCalculator,
  bottomBar,
  extraPackages,
}) => {
  const { current, ref: formRef, isVisible: isFormVisible } = useRefInView({
    threshold: 0,
  });

  return (
    <LandingLayout>
      <PageHead SEO={SEO} />
      <Hero
        title={hero.title}
        description={hero.description}
        secondarySlotTitle={hero.formTitle}
        backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
        secondarySlot={(
          <div ref={formRef}>
            <CreateEvaluation
              onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
            />
          </div>
        )}
      />

      <OurServicesSection
        {...ourServicesSection}
      />

      <PageSection padding={[SpacingName.BIGGER, 0]}>
        <PageSection.Wrapper>
          <PageSection.Title
            content={savingCalculator.title}
            textAlign={TextAlign.CENTER}
          />
          <PageSection.Description
            text={savingCalculator.intro}
            size={TypographyName.H4}
            textAlign={TextAlign.CENTER}
            color="neutrals.dark"
          />
          <PageSection.Block>
            <SavingCalculator />
          </PageSection.Block>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              {...savingCalculator.cta}
              color={ButtonColor.SECONDARY}
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      {extraPackages && extraPackages.packages.length > 0 && (
        <>
          <SectionDivider />
          <PageSection data-ref="extra-packages">
            <PageSection.Wrapper>
              <PageSection.Title
                content={extraPackages.title}
                textAlign={TextAlign.CENTER}
              />
              <PageSection.Description
                text={extraPackages.description}
                textAlign={TextAlign.CENTER}
              />
              <PageSection.Block>
                {extraPackages.packages.map((extraPackage, i) => (
                  <Spacing
                    key={extraPackages.title}
                    margin={[i === 0 ? 0 : SpacingName.MEDIUM, 0, 0]}
                  >
                    <Stripe
                      title={extraPackage.title}
                      description={extraPackage.description}
                      picture={{
                        src: requireFromStaticAsset(`services/${extraPackage.picture}`),
                        alt: 'alt here',
                      }}
                    />
                  </Spacing>
                ))}
              </PageSection.Block>
            </PageSection.Wrapper>
          </PageSection>
        </>
      )}

      <SectionDivider />

      <InfoSection
        title={infoSection.title}
        reasons={infoSection.reasons}
      />

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {aboutUsSection.title}
          </PageSection.Title>
          <PageSection.Description
            text={aboutUsSection.content}
            textAlign={TextAlign.CENTER}
          />
        </PageSection.Wrapper>
        <PageSection.Wrapper>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              label="Affidati a Dove.it"
              aria-label="Affidati a Dove.it"
              dataRef="about-us-section-button"
              dataTracking="about-us-section-button"
              color={ButtonColor.SECONDARY}
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection data-ref="latest-sold-section">
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {latestSoldProperties.title}
          </PageSection.Title>
        </PageSection.Wrapper>
        <PageSection.Wrapper withoutPad>
          <PageSection.Block>
            <ViewLatestSoldProperties sideSpacing={SpacingName.SMALL} />
          </PageSection.Block>
        </PageSection.Wrapper>
        <PageSection.Wrapper>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              dataRef="latest-sold-section-button"
              dataTracking="latest-sold-section-button"
              color={ButtonColor.SECONDARY}
              label="Vendi il tuo immobile"
              aria-label="Vendi il tuo immobile"
              href={buildSoldPropertiesURLObject().as}
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>

      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content="Le recensioni dei nostri clienti su Trustpilot"
          />
          <PageSection.Description
            textAlign={TextAlign.CENTER}
            text="Il 98% dei nostri clienti ha giudicato il servizio <strong>ECCELLENTE</strong>"
            size={TypographyName.H4}
            color="neutrals.dark"
          />
        </PageSection.Wrapper>
        <PageSection.Wrapper withoutPad>
          <PageSection.Block>
            <ListReviews target={[ReviewTarget.COMPRATORE, ReviewTarget.VENDITORE]} />
          </PageSection.Block>
        </PageSection.Wrapper>
        <PageSection.Wrapper>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              label="Vuoi essere il prossimo?"
              aria-label="Vuoi essere il prossimo?"
              dataRef="review-section-button"
              dataTracking="review-section-button"
              color={ButtonColor.SECONDARY}
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>

          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={press.title}
          />
          <PageSection.Block>
            <LogoList
              items={press.items
                .map((item) => ({
                  ...item,
                  picture: requireFromStaticAsset(item.picture),
                }))}
              inlineItems={6}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
        {press.cta && (
        <PageSection.Wrapper>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              label={press.cta.label}
              aria-label={press.cta['aria-label']}
              dataRef={press.cta.dataRef}
              dataTracking={press.cta.dataTracking}
              color={ButtonColor.SECONDARY}
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
        )}
      </PageSection>

      <BottomBar
        hiddenFrom={BreakpointQueryName.MD}
        expanded={!isFormVisible}
        label={bottomBar.label}
        title={bottomBar.title}
        onClick={scrollToRef(current)}
      />
    </LandingLayout>
  );
};

export const getServerSideProps = withGetServerSideError<LandingCitySellerProps>(async ({ query }) => {
  const [common, press, pageData, partnerData, services] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('press.yml'),
    importDynamicData('pages/landing-city-seller.yml'),
    getPartnersData(getTrafficSource(query), 'landing-city-seller.yml', (query.utm_campaign as string)),
    getServices(),
  ]);

  const city = query.city?.toString();

  if (city && !pageData.variants[city]) {
    return {
      redirect: {
        destination: LANDING_CITY_SELLER_AS,
        permanent: false,
      },
    };
  }

  const mergedPageData = {
    ...(merge(common, pageData, (pageData.variants[city] ?? {}))),
    ...partnerData,
  };

  return {
    props: {
      ...mergedPageData,
      press: {
        ...mergedPageData.press,
        items: press.items,
      },
      ourServicesSection: {
        ...common.ourServicesSection,
        // remove urlObject from our services
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        services: services.map(({ urlObject, ...rest }) => rest),
      },
    },
  };
});

export default withError(LandingCitySeller);
